import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Button,
  Text,
  VStack,
  keyframes,
} from "@chakra-ui/react";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * DemoFlowModal — renders centered Chakra Modal for demo steps.
 *
 * Props:
 * - isOpen: boolean
 * - stepConfig: the step config object from demoFlow.js
 * - onAction: (actionId: string) => void — called when a button is clicked
 * - onEndDemo: () => void
 */
const DemoFlowModal = ({ isOpen, stepConfig, onAction, onEndDemo }) => {
  if (!isOpen || !stepConfig || stepConfig.modalType !== "center") return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onEndDemo}
      isCentered
      size="md"
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <ModalOverlay bg="blackAlpha.500" />
      <ModalContent
        borderRadius="16px"
        mx={4}
        fontFamily="Geist, sans-serif"
        py={6}
        px={2}
      >
        <ModalBody>
          <VStack spacing={4} align="center" textAlign="center">
            {/* Heading */}
            {stepConfig.heading && (
              <Text fontSize="xl" fontWeight="700" color="#0C001C">
                {stepConfig.heading}
              </Text>
            )}

            {/* Animated subtitle (for POINTS_CELEBRATION) */}
            {stepConfig.subtitle && (
              <Text
                fontSize="2xl"
                fontWeight="700"
                color="#4C47CC"
                animation={`${fadeInUp} 0.6s ease-out`}
              >
                {stepConfig.subtitle}
              </Text>
            )}

            {/* Body */}
            {stepConfig.body && (
              <Text fontSize="sm" color="#6B7280" lineHeight="1.6">
                {stepConfig.body}
              </Text>
            )}

            {/* Buttons */}
            {stepConfig.buttons?.length > 0 && (
              <VStack spacing={3} w="100%" pt={2}>
                {stepConfig.buttons.map((btn) => (
                  <Button
                    key={btn.action}
                    w="100%"
                    bg={btn.variant === "primary" ? "#4C47CC" : "transparent"}
                    color={btn.variant === "primary" ? "white" : "#6B7280"}
                    fontWeight="700"
                    fontSize="sm"
                    borderRadius="8px"
                    py={6}
                    border={btn.variant === "secondary" ? "1px solid" : "none"}
                    borderColor={
                      btn.variant === "secondary" ? "gray.300" : "transparent"
                    }
                    _hover={{
                      bg: btn.variant === "primary" ? "#3A37B3" : "#F3F4F6",
                    }}
                    onClick={() => onAction(btn.action)}
                  >
                    {btn.label}
                  </Button>
                ))}
              </VStack>
            )}

            {/* End Demo link */}
            <Text
              fontSize="xs"
              color="gray.400"
              cursor="pointer"
              _hover={{ color: "gray.600" }}
              onClick={onEndDemo}
              pt={1}
            >
              End Demo
            </Text>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default DemoFlowModal;
